import { BACKEND_URL } from '../config/backendUrl';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { bullhornApi, settingsApi, tenantsApi, getErrorMessage } from '../services/api';
import DefaultLinkExpiry from '../components/settings/DefaultLinkExpiry';
import DefaultBullhornSendType from '../components/settings/DefaultBullhornSendType';
import { parseCcEmails } from '../utils/parseCcEmails';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Link2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Settings as SettingsIcon,
  Shield,
  Building2,
  Palette,
  Globe,
  Sparkles,
  Mail,
  Phone,
  Save,
  Wand2,
  ExternalLink,
  AlertCircle,
  Eye,
  EyeOff,
  Users,
  Key,
  Upload,
  Bell,
  UserCog,
  UserCheck,
  UserX,
  MailOpen,
  Send,
  Clock,
  StickyNote,
  ShieldAlert,
  Search,
} from 'lucide-react';
import { RichTextEditor } from '../components/RichTextEditor';
import { Switch } from '../components/ui/switch';
import { formsApi, complianceApi } from '../services/api';
import CredasIntegrationSection from '../components/CredasIntegrationSection';
import TrustIdIntegrationSection from '../components/TrustIdIntegrationSection';
import EmailDeliverabilitySection from '../components/EmailDeliverabilitySection';

// Sidebar sections — every existing settings card below is gated to exactly
// one of these via `activeSection === '<id>'` rather than being physically
// moved, so each card keeps its original state/handlers untouched.
const SETTINGS_SECTIONS = [
  { id: 'general', label: 'General', description: 'Workspace details and app info', icon: SettingsIcon },
  { id: 'integrations', label: 'Integrations', description: 'Bullhorn, Credas, API access', icon: Link2 },
  { id: 'branding', label: 'Branding', description: 'Logo, colors, email identity', icon: Palette },
  { id: 'notifications', label: 'Notifications', description: 'Email templates and alerts', icon: Bell },
  { id: 'compliance', label: 'Compliance', description: 'Expiring Bullhorn credentials dashboard', icon: ShieldAlert },
  { id: 'users', label: 'Users & Security', description: 'Workspace members and link security', icon: Users },
];

export default function SettingsPage() {
  const { user, tenant, getTenantPath, refreshTenant } = useAuth();
  const navigate = useNavigate();
  const [bullhornStatus, setBullhornStatus] = useState({ connected: false });
  const [bullhornConfig, setBullhornConfig] = useState({
    client_id: '',
    client_secret: '',
    username: '',
    password: '',
    auth_url: 'https://auth.bullhornstaffing.com/oauth/authorize',
    rest_url: 'https://rest.bullhornstaffing.com/rest-services/'
  });
  const [showSecrets, setShowSecrets] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [savingBullhorn, setSavingBullhorn] = useState(false);
  const [companySettings, setCompanySettings] = useState({
    website_url: '',
    company_name: '',
    company_logo_url: '',
    primary_color: '#f97316',
    secondary_color: '#f59e0b',
    tagline: '',
    about: '',
    welcome_message: '',
    support_email: '',
    support_phone: '',
    notification_cc_emails: [],
    completion_notify_sender_enabled: true,
    completion_confirmation_enabled: true,
    expiry_notify_sender_enabled: false,
    email_send_subject: '',
    email_send_body: '',
    email_reminder_subject: '',
    email_reminder_body: '',
    email_complete_subject: '',
    email_complete_body: '',
    default_reminder_days: 3,
    email_sender_mode: 'recruiter',
    email_company_display_name: '',
    email_company_reply_to: '',
    upload_notifications_enabled: false,
    upload_notification_email: '',
  });
  const [notificationCcInput, setNotificationCcInput] = useState('');
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [savingUploadNotifications, setSavingUploadNotifications] = useState(false);
  const [savingExpiryNotifications, setSavingExpiryNotifications] = useState(false);
  const [savingEmailTemplates, setSavingEmailTemplates] = useState(false);
  const [sendingPreview, setSendingPreview] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [resettingUserId, setResettingUserId] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [detectingBranding, setDetectingBranding] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  // Deep-linkable so e.g. the Expiring Credentials dashboard's "Configure"
  // button can land directly on Settings > Compliance via ?section=compliance.
  const [searchParams] = useSearchParams();
  const validSections = SETTINGS_SECTIONS.map((s) => s.id);
  const [activeSection, setActiveSection] = useState(
    validSections.includes(searchParams.get('section')) ? searchParams.get('section') : 'general'
  );

  useEffect(() => {
    loadCompanySettings();
    if (tenant?.id) {
      loadBullhornConfig();
      loadTenantUsers();
    }
  }, [tenant?.id]);

  const loadCompanySettings = async () => {
    try {
      const response = await settingsApi.getCompany();
      setCompanySettings(prev => ({
        ...prev,
        ...response.data,
        notification_cc_emails: response.data?.notification_cc_emails || [],
        completion_notify_sender_enabled: response.data?.completion_notify_sender_enabled !== false,
        completion_confirmation_enabled: response.data?.completion_confirmation_enabled !== false,
        // opt-in: off until an admin turns it on
        expiry_notify_sender_enabled: response.data?.expiry_notify_sender_enabled === true,
        email_send_subject: response.data?.email_send_subject || '',
        email_send_body: response.data?.email_send_body || '',
        email_reminder_subject: response.data?.email_reminder_subject || '',
        email_reminder_body: response.data?.email_reminder_body || '',
        email_complete_subject: response.data?.email_complete_subject || '',
        email_complete_body: response.data?.email_complete_body || '',
        default_reminder_days: response.data?.default_reminder_days ?? 3,
        email_sender_mode: response.data?.email_sender_mode || 'recruiter',
        email_company_display_name: response.data?.email_company_display_name || '',
        email_company_reply_to: response.data?.email_company_reply_to || '',
        upload_notifications_enabled: response.data?.upload_notifications_enabled || false,
        upload_notification_email: response.data?.upload_notification_email || '',
        reminders_only_approved_placements: response.data?.reminders_only_approved_placements || false,
      }));
      setNotificationCcInput((response.data?.notification_cc_emails || []).join(', '));
      if (response.data.website_url) {
        setWebsiteUrl(response.data.website_url);
      }
    } catch (error) {
      console.error('Failed to load company settings:', error);
    }
  };

  const loadTenantUsers = async () => {
    if (!tenant?.id || user?.is_master_admin) return;
    setLoadingUsers(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/tenants/${tenant.id}/users`);
      setTenantUsers(data.users || []);
    } catch (error) {
      console.error('Failed to load tenant users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const saveNotificationRecipients = async () => {
    setSavingNotifications(true);
    try {
      let emails;
      try {
        emails = parseCcEmails(notificationCcInput);
      } catch (parseError) {
        toast.error(parseError.message);
        setSavingNotifications(false);
        return;
      }
      await settingsApi.updateCompany({
        notification_cc_emails: emails,
        completion_notify_sender_enabled: companySettings.completion_notify_sender_enabled !== false,
      });
      setCompanySettings(prev => ({ ...prev, notification_cc_emails: emails }));
      setNotificationCcInput(emails.join(', '));
      toast.success(emails.length === 0
        ? 'Additional recipients cleared'
        : `Saved — ${emails.length} additional recipient${emails.length === 1 ? '' : 's'} will be CC'd on completion emails`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save notification recipients'));
    } finally {
      setSavingNotifications(false);
    }
  };

  const saveExpiryNotifications = async () => {
    setSavingExpiryNotifications(true);
    try {
      const enabled = companySettings.expiry_notify_sender_enabled === true;
      await settingsApi.updateCompany({ expiry_notify_sender_enabled: enabled });
      toast.success(enabled ? 'Senders will be emailed when their documents expire' : 'Expiry notifications are off');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save expiry notifications'));
    } finally {
      setSavingExpiryNotifications(false);
    }
  };

  const saveUploadNotifications = async () => {
    setSavingUploadNotifications(true);
    try {
      const email = (companySettings.upload_notification_email || '').trim();
      if (companySettings.upload_notifications_enabled && !email) {
        toast.error('Please enter a notification email address');
        setSavingUploadNotifications(false);
        return;
      }
      if (email && !/^\S+@\S+\.\S+$/.test(email)) {
        toast.error('Please enter a valid email address');
        setSavingUploadNotifications(false);
        return;
      }
      await settingsApi.updateCompany({
        upload_notifications_enabled: companySettings.upload_notifications_enabled,
        upload_notification_email: email,
      });
      toast.success(companySettings.upload_notifications_enabled
        ? 'Upload notifications enabled'
        : 'Upload notifications disabled');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save upload notification settings'));
    } finally {
      setSavingUploadNotifications(false);
    }
  };


  const triggerUserPasswordReset = async (targetUser) => {
    if (!tenant?.id) return;
    if (!window.confirm(`Send a password reset link to ${targetUser.email}?\n\nThey'll receive an email with a secure link valid for 1 hour.`)) {
      return;
    }
    setResettingUserId(targetUser.id);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/tenants/${tenant.id}/users/${targetUser.id}/send-password-reset`
      );
      toast.success(data.message || 'Password reset email sent');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send password reset'));
    } finally {
      setResettingUserId(null);
    }
  };

  const loadBullhornConfig = async () => {
    if (!tenant?.id) return;
    try {
      const response = await tenantsApi.getBullhornConfig(tenant.id);
      const isConnected = response.data.is_connected;
      const hasCredentials = response.data.has_complete_credentials || response.data.has_credentials;
      
      setBullhornStatus({
        connected: isConnected,
        hasValidToken: response.data.has_valid_token,
        hasCompleteCredentials: response.data.has_complete_credentials,
        last_sync_at: response.data.last_sync_at,
        error: response.data.connection_error
      });
      // Don't overwrite form fields with masked values
      if (response.data.has_credentials) {
        setBullhornConfig(prev => ({
          ...prev,
          auth_url: response.data.auth_url || prev.auth_url,
          rest_url: response.data.rest_url || prev.rest_url,
        }));
      }
      
      // Auto-connect if credentials exist but not connected
      if (hasCredentials && !isConnected) {
        try {
          const connectRes = await tenantsApi.autoConnectBullhorn(tenant.id);
          if (connectRes.data.auto_connected) {
            setBullhornStatus(prev => ({ ...prev, connected: true, error: null }));
            toast.success('Bullhorn connected automatically');
          }
        } catch (err) {
          // Auto-connect is best-effort; recruiter can connect manually if it fails.
          console.debug('Bullhorn auto-connect skipped:', err?.message);
        }
      }
    } catch (error) {
      console.error('Failed to load Bullhorn config:', error);
      try {
        const legacyResponse = await bullhornApi.status();
        setBullhornStatus(legacyResponse.data);
      } catch (e) {
        setBullhornStatus({ connected: false });
      }
    }
  };

  const saveBullhornConfig = async () => {
    if (!tenant?.id) return;
    if (!bullhornConfig.client_id || !bullhornConfig.client_secret || !bullhornConfig.username || !bullhornConfig.password) {
      toast.error('Please fill in all Bullhorn credentials');
      return;
    }

    setSavingBullhorn(true);
    try {
      await tenantsApi.saveBullhornConfig(tenant.id, bullhornConfig);
      toast.success('Bullhorn credentials saved! Click "Connect" to authenticate.');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save Bullhorn credentials'));
    } finally {
      setSavingBullhorn(false);
    }
  };

  const connectBullhorn = async () => {
    if (!tenant?.id) return;
    
    setConnecting(true);
    try {
      const response = await tenantsApi.connectBullhorn(tenant.id);
      setBullhornStatus({ connected: true });
      toast.success(response.data.message || 'Connected to Bullhorn!');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to connect to Bullhorn'));
      setBullhornStatus({ connected: false, error: getErrorMessage(error) });
    } finally {
      setConnecting(false);
    }
  };

  const handleQuickConnect = async () => {
    if (!tenant?.id) return;
    if (!bullhornConfig.client_id || !bullhornConfig.client_secret || !bullhornConfig.username || !bullhornConfig.password) {
      toast.error('Please fill in all 4 credential fields');
      return;
    }

    setConnecting(true);
    try {
      // Save credentials first
      await tenantsApi.saveBullhornConfig(tenant.id, bullhornConfig);
      
      // Then connect
      const response = await tenantsApi.connectBullhorn(tenant.id);
      setBullhornStatus({ connected: true });
      toast.success('Successfully connected to Bullhorn!');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to connect to Bullhorn'));
      setBullhornStatus({ connected: false, error: getErrorMessage(error) });
    } finally {
      setConnecting(false);
    }
  };

  const handleAutoDetect = async () => {
    if (!websiteUrl.trim()) {
      toast.error('Please enter a website URL');
      return;
    }

    setDetectingBranding(true);
    try {
      const response = await settingsApi.autoDetect(websiteUrl);
      const detected = response.data;
      
      setCompanySettings(prev => ({
        ...prev,
        website_url: detected.website_url || websiteUrl,
        company_name: detected.company_name || prev.company_name,
        company_logo_url: detected.company_logo_url || prev.company_logo_url,
        primary_color: detected.primary_color || prev.primary_color,
        secondary_color: detected.secondary_color || prev.secondary_color,
        tagline: detected.tagline || prev.tagline,
        about: detected.about || prev.about,
      }));
      
      toast.success('Branding detected! Review the details below and save.');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to detect branding from website'));
    } finally {
      setDetectingBranding(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-uploading the same filename
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be smaller than 2 MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(
        `${BACKEND_URL}/api/settings/company/logo`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setCompanySettings((prev) => ({ ...prev, company_logo_url: data.logo_url }));
      toast.success('Logo uploaded');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to upload logo'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const sendEmailPreview = async (templateType) => {
    setSendingPreview(templateType);
    try {
      // Save current edits first so the preview reflects what the admin
      // is actually seeing in the editor (not stale persisted state).
      await settingsApi.updateCompany({
        email_send_subject: companySettings.email_send_subject || '',
        email_send_body: companySettings.email_send_body || '',
        email_reminder_subject: companySettings.email_reminder_subject || '',
        email_reminder_body: companySettings.email_reminder_body || '',
        email_complete_subject: companySettings.email_complete_subject || '',
        email_complete_body: companySettings.email_complete_body || '',
      });
      const { data } = await settingsApi.sendEmailPreview(templateType);
      toast.success(`Test email sent to ${data.sent_to} — check your inbox`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to send test email'));
    } finally {
      setSendingPreview(null);
    }
  };

  const saveEmailTemplates = async () => {
    setSavingEmailTemplates(true);
    try {
      const days = parseInt(companySettings.default_reminder_days, 10);
      const safeDays = Number.isFinite(days) && days > 0 && days <= 90 ? days : 3;
      await settingsApi.updateCompany({
        email_send_subject: companySettings.email_send_subject || '',
        email_send_body: companySettings.email_send_body || '',
        email_reminder_subject: companySettings.email_reminder_subject || '',
        email_reminder_body: companySettings.email_reminder_body || '',
        email_complete_subject: companySettings.email_complete_subject || '',
        email_complete_body: companySettings.email_complete_body || '',
        completion_confirmation_enabled: companySettings.completion_confirmation_enabled !== false,
        default_reminder_days: safeDays,
        reminders_only_approved_placements: !!companySettings.reminders_only_approved_placements,
        email_sender_mode: companySettings.email_sender_mode || 'recruiter',
        email_company_display_name: companySettings.email_company_display_name || '',
        email_company_reply_to: companySettings.email_company_reply_to || '',
      });
      setCompanySettings(prev => ({ ...prev, default_reminder_days: safeDays }));
      toast.success('Email templates saved');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save email templates'));
    } finally {
      setSavingEmailTemplates(false);
    }
  };

  const saveCompanySettings = async () => {
    setSavingSettings(true);
    try {
      await settingsApi.updateCompany(companySettings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save settings'));
    } finally {
      setSavingSettings(false);
    }
  };

  const API_URL = BACKEND_URL || '';

  const isAdmin = user?.role === 'admin' || user?.is_master_admin;
  if (!isAdmin) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">You don't have access to this page.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="settings-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="font-heading text-3xl font-bold text-foreground">Settings</h1>
          <p className="text-muted-foreground font-body mt-1">
            Manage your {tenant?.name || 'organization'} branding and integrations
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-6 items-start">
          {/* Sidebar nav */}
          <nav className="w-full md:w-56 flex-shrink-0 md:sticky md:top-6 flex md:flex-col gap-1 overflow-x-auto md:overflow-visible pb-2 md:pb-0" data-testid="settings-nav">
            {SETTINGS_SECTIONS.map((section) => {
              const Icon = section.icon;
              const active = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setActiveSection(section.id)}
                  data-testid={`settings-nav-${section.id}`}
                  className={`flex-shrink-0 md:flex-shrink text-left flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-colors whitespace-nowrap md:whitespace-normal ${
                    active ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <div className="min-w-0 hidden md:block">
                    <div className="text-sm font-medium leading-tight">{section.label}</div>
                    <div className={`text-xs leading-tight truncate ${active ? 'text-primary-foreground/80' : 'text-muted-foreground/80'}`}>
                      {section.description}
                    </div>
                  </div>
                  <span className="text-sm font-medium md:hidden">{section.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Active section content */}
          <div className="flex-1 min-w-0 space-y-8">

        {/* Tenant Info */}
        {activeSection === 'general' && tenant && (
          <div className="neumorphic rounded-xl p-6 bg-gradient-to-br from-primary/5 to-amber-500/5 border border-primary/10">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-amber-500 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="font-heading font-semibold text-lg">{tenant.name}</h2>
                <p className="text-sm text-muted-foreground">
                  Workspace: <span className="font-mono bg-muted px-2 py-0.5 rounded">/{tenant.slug}</span>
                </p>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Users className="w-4 h-4" />
                <span>{tenant.user_count || 1} user(s)</span>
              </div>
            </div>
          </div>
        )}

        {/* Features */}
        {activeSection === 'general' && <FeaturesSection tenant={tenant} refreshTenant={refreshTenant} />}

        {/* Bullhorn Integration - Per-Tenant Config (Hidden for Master Admin) */}
        {activeSection === 'integrations' && !user?.is_master_admin && (
        <div className="neumorphic rounded-xl p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#0066B3] flex items-center justify-center">
              <Link2 className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="font-heading font-semibold text-lg">Bullhorn ATS</h2>
              <p className="text-sm text-muted-foreground">Connect your organization's Bullhorn instance</p>
            </div>
            {bullhornStatus.connected ? (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-700">Connected</span>
              </div>
            ) : bullhornStatus.hasCompleteCredentials ? (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 rounded-full">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-700">Credentials Set - Not Connected</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 rounded-full">
                <XCircle className="w-4 h-4 text-red-600" />
                <span className="text-sm font-medium text-red-700">Not Connected</span>
              </div>
            )}
          </div>

          {bullhornStatus.error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle className="w-4 h-4 inline mr-2" />
              {bullhornStatus.error}
            </div>
          )}

          {isAdmin ? (
            <div className="space-y-6">
              {/* Credentials Form - Simplified */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    API Credentials
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowSecrets(!showSecrets)}
                  >
                    {showSecrets ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                    {showSecrets ? 'Hide' : 'Show'}
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Client ID</Label>
                    <Input
                      type={showSecrets ? 'text' : 'password'}
                      value={bullhornConfig.client_id}
                      onChange={(e) => setBullhornConfig(prev => ({ ...prev, client_id: e.target.value }))}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      className="font-mono text-sm"
                      data-testid="bullhorn-client-id"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Client Secret</Label>
                    <Input
                      type={showSecrets ? 'text' : 'password'}
                      value={bullhornConfig.client_secret}
                      onChange={(e) => setBullhornConfig(prev => ({ ...prev, client_secret: e.target.value }))}
                      placeholder="Your client secret"
                      className="font-mono text-sm"
                      data-testid="bullhorn-client-secret"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>API Username</Label>
                    <Input
                      type="text"
                      value={bullhornConfig.username}
                      onChange={(e) => setBullhornConfig(prev => ({ ...prev, username: e.target.value }))}
                      placeholder="company.api.user"
                      className="font-mono text-sm"
                      data-testid="bullhorn-username"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>API Password</Label>
                    <Input
                      type={showSecrets ? 'text' : 'password'}
                      value={bullhornConfig.password}
                      onChange={(e) => setBullhornConfig(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="Your API password"
                      className="font-mono text-sm"
                      data-testid="bullhorn-password"
                    />
                  </div>
                </div>

                {/* Region Selector */}
                <div className="space-y-2 pt-2 border-t">
                  <Label>Bullhorn Region</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={bullhornConfig.auth_url?.includes('auth.bullhorn') ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setBullhornConfig(prev => ({ 
                        ...prev, 
                        auth_url: 'https://auth.bullhornstaffing.com/oauth/authorize',
                        rest_url: 'https://rest.bullhornstaffing.com/rest-services/'
                      }))}
                    >
                      US / Default
                    </Button>
                    <Button
                      type="button"
                      variant={bullhornConfig.auth_url?.includes('auth-emea') ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setBullhornConfig(prev => ({ 
                        ...prev, 
                        auth_url: 'https://auth-emea.bullhornstaffing.com/oauth/authorize',
                        rest_url: 'https://rest-emea.bullhornstaffing.com/rest-services/'
                      }))}
                    >
                      EMEA
                    </Button>
                    <Button
                      type="button"
                      variant={bullhornConfig.auth_url?.includes('auth-apac') ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setBullhornConfig(prev => ({ 
                        ...prev, 
                        auth_url: 'https://auth-apac.bullhornstaffing.com/oauth/authorize',
                        rest_url: 'https://rest-apac.bullhornstaffing.com/rest-services/'
                      }))}
                    >
                      APAC
                    </Button>
                  </div>
                </div>
              </div>

              {/* One-Click Connect */}
              <Button 
                onClick={handleQuickConnect}
                disabled={connecting || !bullhornConfig.client_id || !bullhornConfig.client_secret || !bullhornConfig.username || !bullhornConfig.password}
                className="w-full btn-primary h-12 text-base"
                data-testid="bullhorn-connect"
              >
                {connecting ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Link2 className="w-5 h-5 mr-2" />
                    Connect to Bullhorn
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                Credentials are encrypted and stored securely. Only admins can view or modify these settings.
              </p>

              <DefaultBullhornSendType />
            </div>
          ) : (
            <div className="p-4 bg-muted/30 rounded-lg text-center">
              <p className="text-muted-foreground">
                Contact your organization admin to configure Bullhorn integration.
              </p>
            </div>
          )}
        </div>
        )}

        {/* Master Admin Notice */}
        {activeSection === 'integrations' && user?.is_master_admin && (
          <div className="neumorphic rounded-xl p-6 bg-amber-50 border border-amber-200">
            <div className="flex items-center gap-4">
              <AlertCircle className="w-6 h-6 text-amber-600" />
              <div>
                <h3 className="font-medium text-amber-800">Platform Administrator</h3>
                <p className="text-sm text-amber-700">
                  Bullhorn configuration is managed per-organization. Go to the <a href="/master" className="underline font-medium">Master Admin Panel</a> to configure Bullhorn for each organization.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Auto-Detect Branding */}
        {activeSection === 'branding' && (
        <div className="neumorphic rounded-xl p-6 border-2 border-dashed border-primary/30 bg-gradient-to-br from-primary/5 to-amber-500/5">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-amber-500 flex items-center justify-center">
              <Wand2 className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="font-heading font-semibold text-lg">Auto-Detect Branding</h2>
              <p className="text-sm text-muted-foreground">Enter your website URL to automatically extract your brand</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Company Website
              </Label>
              <div className="flex gap-2">
                <Input
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="www.yourcompany.com"
                  className="flex-1 input-focus"
                  data-testid="website-url-input"
                />
                <Button
                  onClick={handleAutoDetect}
                  disabled={detectingBranding || !websiteUrl.trim()}
                  className="btn-primary min-w-[160px]"
                  data-testid="auto-detect-button"
                >
                  {detectingBranding ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      Detecting...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Auto-Detect
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                We'll extract your logo, brand colors, tagline, and company info automatically
              </p>
            </div>
          </div>
        </div>
        )}

        {/* Company Branding */}
        {activeSection === 'branding' && (
        <div className="neumorphic rounded-xl p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-amber-500 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="font-heading font-semibold text-lg">Company Branding</h2>
              <p className="text-sm text-muted-foreground">Customize how candidates see your portal</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Logo Preview + Upload */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                Company Logo
              </Label>
              <div className="flex items-start gap-4">
                {companySettings.company_logo_url ? (
                  <div
                    className="w-24 h-24 rounded-xl border-2 border-border flex items-center justify-center overflow-hidden shadow-sm flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #334155 0 50%, #f8fafc 50% 100%)' }}
                  >
                    <img 
                      src={`${API_URL}${companySettings.company_logo_url}`} 
                      alt="Company logo" 
                      className="max-w-full max-h-full object-contain"
                      data-testid="company-logo-preview"
                    />
                  </div>
                ) : (
                  <div className="w-24 h-24 rounded-xl border-2 border-dashed border-border flex items-center justify-center bg-muted/30 flex-shrink-0">
                    <Building2 className="w-10 h-10 text-muted-foreground/50" />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('logo-upload-input')?.click()}
                      disabled={uploadingLogo}
                      data-testid="upload-logo-btn"
                    >
                      <Upload className="w-3.5 h-3.5 mr-1.5" />
                      {uploadingLogo ? 'Uploading…' : 'Upload logo'}
                    </Button>
                    {companySettings.company_logo_url && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setCompanySettings(prev => ({ ...prev, company_logo_url: '' }))}
                        data-testid="remove-logo-btn"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                  <input
                    id="logo-upload-input"
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    className="hidden"
                    onChange={handleLogoUpload}
                  />
                  <p className="text-xs text-muted-foreground">
                    PNG, JPG, SVG or WebP. Max 2 MB. Or use <strong>Auto-detect</strong> above to fetch it from your website.
                  </p>
                </div>
              </div>
            </div>

            {/* Company Name */}
            <div className="space-y-2">
              <Label htmlFor="company_name">Company Name</Label>
              <Input
                id="company_name"
                value={companySettings.company_name}
                onChange={(e) => setCompanySettings(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder="Your Company Name"
                className="input-focus"
                data-testid="company-name-input"
              />
            </div>

            {/* Tagline */}
            <div className="space-y-2">
              <Label htmlFor="tagline">Tagline</Label>
              <Input
                id="tagline"
                value={companySettings.tagline || ''}
                onChange={(e) => setCompanySettings(prev => ({ ...prev, tagline: e.target.value }))}
                placeholder="Your company's tagline or slogan"
                className="input-focus"
                data-testid="tagline-input"
              />
            </div>

            {/* About */}
            <div className="space-y-2">
              <Label htmlFor="about">About Company</Label>
              <Textarea
                id="about"
                value={companySettings.about || ''}
                onChange={(e) => setCompanySettings(prev => ({ ...prev, about: e.target.value }))}
                placeholder="Brief description of your company"
                rows={3}
                className="input-focus"
                data-testid="about-input"
              />
            </div>

            {/* Brand Colors */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Primary Color
                </Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={companySettings.primary_color}
                    onChange={(e) => setCompanySettings(prev => ({ ...prev, primary_color: e.target.value }))}
                    className="w-10 h-10 rounded-lg cursor-pointer border border-border"
                    data-testid="primary-color-input"
                  />
                  <Input
                    value={companySettings.primary_color}
                    onChange={(e) => setCompanySettings(prev => ({ ...prev, primary_color: e.target.value }))}
                    placeholder="#f97316"
                    className="font-mono text-sm"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Secondary Color
                </Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={companySettings.secondary_color}
                    onChange={(e) => setCompanySettings(prev => ({ ...prev, secondary_color: e.target.value }))}
                    className="w-10 h-10 rounded-lg cursor-pointer border border-border"
                    data-testid="secondary-color-input"
                  />
                  <Input
                    value={companySettings.secondary_color}
                    onChange={(e) => setCompanySettings(prev => ({ ...prev, secondary_color: e.target.value }))}
                    placeholder="#f59e0b"
                    className="font-mono text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Welcome Message */}
            <div className="space-y-2">
              <Label htmlFor="welcome_message">Welcome Message</Label>
              <Textarea
                id="welcome_message"
                value={companySettings.welcome_message || ''}
                onChange={(e) => setCompanySettings(prev => ({ ...prev, welcome_message: e.target.value }))}
                placeholder="Welcome! Please complete the following documents to proceed with your onboarding."
                rows={2}
                className="input-focus"
                data-testid="welcome-message-input"
              />
              <p className="text-xs text-muted-foreground">Shown to candidates on the portal landing page</p>
            </div>

            {/* Support Contact */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Support Email
                </Label>
                <Input
                  value={companySettings.support_email || ''}
                  onChange={(e) => setCompanySettings(prev => ({ ...prev, support_email: e.target.value }))}
                  placeholder="support@yourcompany.com"
                  type="email"
                  data-testid="support-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Support Phone
                </Label>
                <Input
                  value={companySettings.support_phone || ''}
                  onChange={(e) => setCompanySettings(prev => ({ ...prev, support_phone: e.target.value }))}
                  placeholder="+44 123 456 7890"
                  data-testid="support-phone-input"
                />
              </div>
            </div>

            {/* Preview */}
            <div className="border rounded-xl overflow-hidden">
              <div 
                className="p-6 text-white text-center"
                style={{ background: `linear-gradient(135deg, ${companySettings.primary_color} 0%, ${companySettings.secondary_color} 100%)` }}
              >
                {companySettings.company_logo_url && (
                  <div className="w-16 h-16 mx-auto mb-3 rounded-xl p-1.5 shadow-lg bg-white/15 border border-white/20">
                    <img 
                      src={`${API_URL}${companySettings.company_logo_url}`}
                      alt="Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
                <p className="font-bold text-xl">{companySettings.company_name || 'Your Company Name'}</p>
                {companySettings.tagline && (
                  <p className="text-sm opacity-90 mt-1">{companySettings.tagline}</p>
                )}
              </div>
              <div className="p-4 bg-muted/30">
                {companySettings.about && (
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{companySettings.about}</p>
                )}
                <p className="text-sm text-muted-foreground text-center">
                  {companySettings.welcome_message || 'Your welcome message will appear here...'}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button 
                onClick={saveCompanySettings}
                disabled={savingSettings}
                className="btn-primary flex-1"
                data-testid="save-branding-button"
              >
                {savingSettings ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Branding Settings
              </Button>
              <Button 
                variant="outline"
                onClick={() => {
                  window.open(getTenantPath('/settings/portal-preview'), '_blank', 'noopener,noreferrer');
                }}
                className="flex items-center gap-2"
                data-testid="preview-portal-button"
              >
                <ExternalLink className="w-4 h-4" />
                Preview Portal
              </Button>
            </div>
          </div>
        </div>
        )}

        {/* Email Customization (Send / Reminder / Complete) */}
        {activeSection === 'notifications' && !user?.is_master_admin && (
        <div className="neumorphic rounded-xl p-6" data-testid="email-customization-card">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center">
              <MailOpen className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="font-heading font-semibold text-lg">Email Customization</h2>
              <p className="text-sm text-muted-foreground">
                Personalise the emails sent to candidates. Your company logo (set above) automatically replaces the HyreDocs wordmark.
              </p>
            </div>
          </div>

          {!companySettings.company_logo_url && (
            <div className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <strong>Tip:</strong> Upload a Company Logo in the Branding section above and it will appear at the top of every email you send.
              </div>
            </div>
          )}

          <div className="mb-6 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">
            <p className="font-medium text-slate-800 mb-1">Available placeholders</p>
            <p className="leading-relaxed">
              Use these tokens — they are filled in automatically when an email is sent:{' '}
              <code className="px-1.5 py-0.5 bg-white border rounded text-[11px]">{'{recipient_name}'}</code>{' '}
              <code className="px-1.5 py-0.5 bg-white border rounded text-[11px]">{'{sender_name}'}</code>{' '}
              <code className="px-1.5 py-0.5 bg-white border rounded text-[11px]">{'{company_name}'}</code>{' '}
              <code className="px-1.5 py-0.5 bg-white border rounded text-[11px]">{'{document_name}'}</code>{' '}
              <code className="px-1.5 py-0.5 bg-white border rounded text-[11px]">{'{completion_link}'}</code>{' '}
              <code className="px-1.5 py-0.5 bg-white border rounded text-[11px]">{'{expiry_date}'}</code>
            </p>
          </div>

          {/* Default reminder days */}
          <div className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-lg" data-testid="default-reminder-days-block">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-4 h-4 text-slate-600" />
              <Label htmlFor="default-reminder-days" className="font-medium">Default reminder cadence</Label>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              How many days after sending should "Send Reminder" pre-select by default? Recruiters can still send reminders manually at any time.
            </p>
            <div className="flex items-center gap-2 max-w-xs">
              <Input
                id="default-reminder-days"
                type="number"
                min="1"
                max="90"
                value={companySettings.default_reminder_days}
                onChange={(e) =>
                  setCompanySettings((prev) => ({ ...prev, default_reminder_days: e.target.value }))
                }
                data-testid="default-reminder-days-input"
              />
              <span className="text-sm text-muted-foreground whitespace-nowrap">days</span>
            </div>

            {/* Only send reminders for approved placements */}
            <div className="mt-4 flex items-center gap-3">
              <Switch
                checked={companySettings.reminders_only_approved_placements || false}
                onCheckedChange={(checked) =>
                  setCompanySettings(prev => ({ ...prev, reminders_only_approved_placements: checked }))
                }
                data-testid="reminders-approved-only-toggle"
              />
              <div>
                <Label className="font-medium">Only send reminders for approved placements</Label>
                <p className="text-xs text-muted-foreground">
                  When enabled, reminders will only be sent for documents linked to placements with status "Approved" in Bullhorn. Documents not linked to placements are unaffected.
                </p>
              </div>
            </div>
          </div>

          {/* Sender alias — choose between per-recruiter and a single
              company-wide mailbox.  Mail still goes out from our
              notifications mailbox, but the From-display + Reply-To are
              masked so the candidate sees "Compliance via HyreDocs"
              etc. instead of an individual recruiter's name. */}
          <div className="mb-6 p-4 rounded-lg border bg-slate-50 space-y-3" data-testid="sender-alias-block">
            <div>
              <Label className="text-base font-medium">Sender identity</Label>
              <p className="text-sm text-muted-foreground">
                Choose how candidate-facing emails appear in their inbox.
                The actual From-address stays the secure HyreDocs
                notifications mailbox; only the display name and Reply-To
                are customised.
              </p>
            </div>
            <div className="space-y-2">
              <label className="flex items-start gap-3 p-3 rounded-md border bg-white cursor-pointer hover:border-orange-300">
                <input
                  type="radio"
                  name="email_sender_mode"
                  value="recruiter"
                  checked={companySettings.email_sender_mode === 'recruiter'}
                  onChange={() => setCompanySettings((p) => ({ ...p, email_sender_mode: 'recruiter' }))}
                  className="mt-1"
                  data-testid="sender-mode-recruiter"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium">Individual recruiter <span className="text-xs text-muted-foreground">(default)</span></div>
                  <div className="text-xs text-muted-foreground">e.g. &ldquo;John Smith - {companySettings.company_name || 'Company'} via HyreDocs&rdquo; · Reply-To = recruiter&rsquo;s email</div>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-md border bg-white cursor-pointer hover:border-orange-300">
                <input
                  type="radio"
                  name="email_sender_mode"
                  value="company"
                  checked={companySettings.email_sender_mode === 'company'}
                  onChange={() => setCompanySettings((p) => ({ ...p, email_sender_mode: 'company' }))}
                  className="mt-1"
                  data-testid="sender-mode-company"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium">Company alias</div>
                  <div className="text-xs text-muted-foreground">e.g. &ldquo;Compliance via HyreDocs&rdquo; · Reply-To = central mailbox</div>
                </div>
              </label>
            </div>
            {companySettings.email_sender_mode === 'company' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                <div className="space-y-1.5">
                  <Label htmlFor="company-display-name" className="text-sm">Display name</Label>
                  <Input
                    id="company-display-name"
                    placeholder="e.g. Compliance"
                    value={companySettings.email_company_display_name || ''}
                    onChange={(e) =>
                      setCompanySettings((p) => ({ ...p, email_company_display_name: e.target.value }))
                    }
                    data-testid="email-company-display-name"
                  />
                  <p className="text-xs text-muted-foreground">Shown as &ldquo;{(companySettings.email_company_display_name || 'Compliance')} - {companySettings.company_name || 'Company'} via HyreDocs&rdquo; in the candidate&rsquo;s inbox.</p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="company-reply-to" className="text-sm">Reply-To address</Label>
                  <Input
                    id="company-reply-to"
                    type="email"
                    placeholder="compliance@yourcompany.co.uk"
                    value={companySettings.email_company_reply_to || ''}
                    onChange={(e) =>
                      setCompanySettings((p) => ({ ...p, email_company_reply_to: e.target.value }))
                    }
                    data-testid="email-company-reply-to"
                  />
                  <p className="text-xs text-muted-foreground">Candidate replies route here instead of the individual recruiter.</p>
                </div>
              </div>
            )}
          </div>

          {/* Three template editors */}
          <div className="space-y-6">
            {/* SEND */}
            <div data-testid="email-template-send">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-md bg-blue-500/10 flex items-center justify-center">
                    <Send className="w-4 h-4 text-blue-600" />
                  </div>
                  <h3 className="font-medium text-base">When a document is sent</h3>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => sendEmailPreview('send')}
                  disabled={sendingPreview === 'send'}
                  data-testid="send-test-email-send-button"
                >
                  {sendingPreview === 'send' ? (
                    <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />Sending…</>
                  ) : (
                    <><Send className="w-3.5 h-3.5 mr-1.5" />Send Test Email</>
                  )}
                </Button>
              </div>
              <div className="space-y-2 mb-3">
                <Label htmlFor="email-send-subject">Subject</Label>
                <Input
                  id="email-send-subject"
                  value={companySettings.email_send_subject}
                  onChange={(e) =>
                    setCompanySettings((prev) => ({ ...prev, email_send_subject: e.target.value }))
                  }
                  placeholder="e.g. {company_name} has sent you {document_name} to complete"
                  data-testid="email-send-subject-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Body</Label>
                <RichTextEditor
                  testId="email-send-body-editor"
                  value={companySettings.email_send_body}
                  onChange={(html) =>
                    setCompanySettings((prev) => ({ ...prev, email_send_body: html }))
                  }
                  placeholder="Hi {recipient_name}, please review and complete {document_name}…"
                  rows={6}
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank to use the default HyreDocs message.
                </p>
              </div>
            </div>

            {/* REMINDER */}
            <div data-testid="email-template-reminder">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-md bg-amber-500/10 flex items-center justify-center">
                    <Bell className="w-4 h-4 text-amber-600" />
                  </div>
                  <h3 className="font-medium text-base">Reminder email</h3>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => sendEmailPreview('reminder')}
                  disabled={sendingPreview === 'reminder'}
                  data-testid="send-test-email-reminder-button"
                >
                  {sendingPreview === 'reminder' ? (
                    <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />Sending…</>
                  ) : (
                    <><Send className="w-3.5 h-3.5 mr-1.5" />Send Test Email</>
                  )}
                </Button>
              </div>
              <div className="space-y-2 mb-3">
                <Label htmlFor="email-reminder-subject">Subject</Label>
                <Input
                  id="email-reminder-subject"
                  value={companySettings.email_reminder_subject}
                  onChange={(e) =>
                    setCompanySettings((prev) => ({ ...prev, email_reminder_subject: e.target.value }))
                  }
                  placeholder="e.g. Reminder — your {document_name} is still pending"
                  data-testid="email-reminder-subject-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Body</Label>
                <RichTextEditor
                  testId="email-reminder-body-editor"
                  value={companySettings.email_reminder_body}
                  onChange={(html) =>
                    setCompanySettings((prev) => ({ ...prev, email_reminder_body: html }))
                  }
                  placeholder="Hi {recipient_name}, just a quick reminder to complete {document_name}…"
                  rows={6}
                />
              </div>
            </div>

            {/* COMPLETE */}
            <div data-testid="email-template-complete">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-md bg-emerald-500/10 flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  </div>
                  <h3 className="font-medium text-base">Completion confirmation</h3>
                  <span className="text-xs text-muted-foreground">— sent to the candidate/contact who completed it</span>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={companySettings.completion_confirmation_enabled !== false}
                    onCheckedChange={(checked) =>
                      setCompanySettings(prev => ({ ...prev, completion_confirmation_enabled: checked }))
                    }
                    data-testid="completion-confirmation-toggle"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => sendEmailPreview('complete')}
                    disabled={sendingPreview === 'complete'}
                    data-testid="send-test-email-complete-button"
                  >
                    {sendingPreview === 'complete' ? (
                      <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />Sending…</>
                    ) : (
                      <><Send className="w-3.5 h-3.5 mr-1.5" />Send Test Email</>
                    )}
                  </Button>
                </div>
              </div>
              <div className="space-y-2 mb-3">
                <Label htmlFor="email-complete-subject">Subject</Label>
                <Input
                  id="email-complete-subject"
                  value={companySettings.email_complete_subject}
                  onChange={(e) =>
                    setCompanySettings((prev) => ({ ...prev, email_complete_subject: e.target.value }))
                  }
                  placeholder="e.g. Thanks {recipient_name}, we received your {document_name}"
                  data-testid="email-complete-subject-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Body</Label>
                <RichTextEditor
                  testId="email-complete-body-editor"
                  value={companySettings.email_complete_body}
                  onChange={(html) =>
                    setCompanySettings((prev) => ({ ...prev, email_complete_body: html }))
                  }
                  placeholder="Hi {recipient_name}, thanks for completing {document_name}. We'll be in touch shortly…"
                  rows={6}
                />
              </div>
            </div>
          </div>

          <div className="pt-6 mt-6 border-t border-slate-200">
            <Button
              onClick={saveEmailTemplates}
              disabled={savingEmailTemplates}
              className="btn-primary"
              data-testid="save-email-templates-button"
            >
              {savingEmailTemplates ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving…</>
              ) : (
                <><Save className="w-4 h-4 mr-2" />Save Email Templates</>
              )}
            </Button>
          </div>
        </div>
        )}

        {/* Completion Notifications */}
        {activeSection === 'notifications' && !user?.is_master_admin && (
        <div className="neumorphic rounded-xl p-6" data-testid="notifications-settings-card">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-teal-600 flex items-center justify-center">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="font-heading font-semibold text-lg">Completion Notifications</h2>
              <p className="text-sm text-muted-foreground">
                Email the sender (the recruiter who sent the document) as soon as it's completed. Add extra recipients below to CC them too.
              </p>
            </div>
            <Switch
              checked={companySettings.completion_notify_sender_enabled !== false}
              onCheckedChange={(checked) =>
                setCompanySettings(prev => ({ ...prev, completion_notify_sender_enabled: checked }))
              }
              data-testid="completion-notify-sender-toggle"
            />
          </div>

          <div className={`space-y-3 ${companySettings.completion_notify_sender_enabled === false ? 'opacity-50 pointer-events-none' : ''}`}>
            <Label htmlFor="notification-cc-input">Additional recipients (CC)</Label>
            <Textarea
              id="notification-cc-input"
              value={notificationCcInput}
              onChange={(e) => setNotificationCcInput(e.target.value)}
              placeholder="hr@yourcompany.com, compliance@yourcompany.com"
              rows={2}
              data-testid="notification-cc-input"
            />
            <p className="text-xs text-muted-foreground">
              Comma-, semicolon-, or newline-separated. Leave blank to notify only the sender.
            </p>
            {companySettings.notification_cc_emails?.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2" data-testid="notification-cc-chips">
                {companySettings.notification_cc_emails.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1.5 text-xs bg-teal-50 text-teal-800 border border-teal-200 rounded-full px-3 py-1"
                  >
                    <Mail className="w-3 h-3" />
                    {email}
                  </span>
                ))}
              </div>
            )}
          </div>
          {/* Outside the dimmed/disabled block above — the toggle itself (on OR
              off) must always be savable, even when the CC list beneath it is
              greyed out because notifications are currently off. */}
          <div className="pt-4">
            <Button
              onClick={saveNotificationRecipients}
              disabled={savingNotifications}
              data-testid="save-notification-cc-button"
            >
              {savingNotifications ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving…</>
              ) : (
                <><Save className="w-4 h-4 mr-2" />Save</>
              )}
            </Button>
          </div>
        </div>
        )}

        {/* Expiry Notifications */}
        {activeSection === 'notifications' && !user?.is_master_admin && (
        <div className="neumorphic rounded-xl p-6" data-testid="expiry-notifications-settings-card">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-amber-600 flex items-center justify-center">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="font-heading font-semibold text-lg">Expiry Notifications</h2>
              <p className="text-sm text-muted-foreground">
                Email the sender (the recruiter who sent the document) when a document expires before the recipient completes it.
              </p>
            </div>
            <Switch
              checked={companySettings.expiry_notify_sender_enabled === true}
              onCheckedChange={(checked) =>
                setCompanySettings(prev => ({ ...prev, expiry_notify_sender_enabled: checked }))
              }
              data-testid="expiry-notify-sender-toggle"
            />
          </div>

          <div className="rounded-lg bg-muted/40 p-4 text-sm space-y-2" data-testid="expiry-notification-example">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">What the sender receives</p>
            <p><span className="text-muted-foreground">Subject:</span> {'<Document name>'} for {'<Jane Candidate>'} has expired</p>
            <p>
              <strong>{'<Document name>'}</strong> for {'<Jane Candidate>'} ({'<jane@example.com>'}) has expired without being completed.
            </p>
            <p className="text-muted-foreground">
              Sent {'<date sent>'} · {'<Never opened / Opened but not finished>'} · Expired {'<date expired>'}
            </p>
            <p className="text-xs text-muted-foreground">Anything in &lt;angle brackets&gt; is filled in from the document.</p>
            <p className="text-muted-foreground">
              Includes a link to the expired documents and a reminder that Resend gives the recipient a fresh link.
              If several of a sender's documents expire on the same day, they get one email listing them all.
              Only documents that were sent, opened or in progress when they expired are included.
            </p>
          </div>

          <div className="pt-4">
            <Button
              onClick={saveExpiryNotifications}
              disabled={savingExpiryNotifications}
              data-testid="save-expiry-notifications-button"
            >
              {savingExpiryNotifications ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving…</>
              ) : (
                <><Save className="w-4 h-4 mr-2" />Save</>
              )}
            </Button>
          </div>
        </div>
        )}

        {/* Upload Notifications */}
        {activeSection === 'notifications' && !user?.is_master_admin && (
        <div className="neumorphic rounded-xl p-6" data-testid="upload-notifications-settings-card">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-emerald-600 flex items-center justify-center">
              <Upload className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="font-heading font-semibold text-lg">Upload Notifications</h2>
              <p className="text-sm text-muted-foreground">
                Get notified when a candidate uploads a CV or credential/qualification via the portal.
              </p>
            </div>
            <Switch
              checked={companySettings.upload_notifications_enabled || false}
              onCheckedChange={(checked) =>
                setCompanySettings(prev => ({ ...prev, upload_notifications_enabled: checked }))
              }
              data-testid="upload-notifications-toggle"
            />
          </div>

          {companySettings.upload_notifications_enabled && (
            <div className="space-y-3">
              <Label htmlFor="upload-notification-email">Notification email address</Label>
              <Input
                id="upload-notification-email"
                type="email"
                value={companySettings.upload_notification_email || ''}
                onChange={(e) =>
                  setCompanySettings(prev => ({ ...prev, upload_notification_email: e.target.value }))
                }
                placeholder="compliance@yourcompany.com"
                data-testid="upload-notification-email-input"
              />
              <p className="text-xs text-muted-foreground">
                This email will receive a notification every time a candidate uploads a CV or credential. You can enter multiple addresses separated by commas.
              </p>
              <div className="pt-2">
                <Button
                  onClick={saveUploadNotifications}
                  disabled={savingUploadNotifications}
                  data-testid="save-upload-notifications-button"
                >
                  {savingUploadNotifications ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" />Save</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Bullhorn Notes (auto-logging behavior on send) */}
        {activeSection === 'integrations' && !user?.is_master_admin && user?.role === 'admin' && (
          <BullhornNotesSection tenant={tenant} refreshTenant={refreshTenant} />
        )}

        {/* Credential expiry reminders */}
        {activeSection === 'notifications' && !user?.is_master_admin && user?.role === 'admin' && (
          <CredentialRemindersSection tenant={tenant} refreshTenant={refreshTenant} />
        )}

        {/* Expiring Credentials dashboard config */}
        {activeSection === 'compliance' && !user?.is_master_admin && user?.role === 'admin' && (
          <ExpiringCredentialsSettingsSection />
        )}

        {/* Credas ID Verification Integration */}
        {activeSection === 'integrations' && !user?.is_master_admin && user?.role === 'admin' && (
          <CredasIntegrationSection />
        )}

        {/* TrustID Right to Work / ID Verification Integration */}
        {activeSection === 'integrations' && !user?.is_master_admin && user?.role === 'admin' && (
          <TrustIdIntegrationSection />
        )}

        {/* Email Deliverability diagnostics (admin-only) */}
        {activeSection === 'notifications' && !user?.is_master_admin && user?.role === 'admin' && (
          <EmailDeliverabilitySection />
        )}

        {/* HyreDocs Users */}
        {activeSection === 'users' && !user?.is_master_admin && (
        <div className="neumorphic rounded-xl p-6" data-testid="tenant-users-card">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-indigo-600 flex items-center justify-center">
              <UserCog className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="font-heading font-semibold text-lg">HyreDocs Users</h2>
              <p className="text-sm text-muted-foreground">
                View your workspace users and send secure password reset links. Adding new users is handled by the HyreDocs team — <a href="mailto:support@hyredocs.com" className="text-primary underline">contact support</a> to provision additional seats.
              </p>
            </div>
          </div>

          {loadingUsers ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Loading users…
            </div>
          ) : tenantUsers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              No users found in this workspace.
            </div>
          ) : (
            <div className="border border-border rounded-lg overflow-hidden">
              <table className="w-full text-sm" data-testid="tenant-users-table">
                <thead className="bg-muted/50">
                  <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider">
                    <th className="px-4 py-2 font-medium">Name</th>
                    <th className="px-4 py-2 font-medium">Email</th>
                    <th className="px-4 py-2 font-medium">Role</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {tenantUsers.map((u) => {
                    const isBullhornOnly = u.auth_provider === 'bullhorn' && !u.password;
                    const inactive = u.is_active === false;
                    const isSelf = u.id === user?.id;
                    return (
                      <tr key={u.id} className={inactive ? 'opacity-60 bg-slate-50' : ''} data-testid={`tenant-user-row-${u.id}`}>
                        <td className="px-4 py-3 font-medium">{u.name || '—'}</td>
                        <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                        <td className="px-4 py-3">
                          {user?.role === 'admin' && !isSelf && !inactive ? (
                            <select
                              value={u.role || 'user'}
                              onChange={async (e) => {
                                const newRole = e.target.value;
                                try {
                                  await settingsApi.updateUserRole(tenant?.id, u.id, newRole);
                                  toast.success(`${u.name || u.email} is now ${newRole}`);
                                  setTenantUsers(prev => prev.map(tu => tu.id === u.id ? { ...tu, role: newRole } : tu));
                                } catch (err) {
                                  toast.error(getErrorMessage(err, 'Failed to update role'));
                                }
                              }}
                              className="text-xs px-2 py-1 rounded border border-border bg-white cursor-pointer"
                              data-testid={`role-select-${u.id}`}
                            >
                              <option value="admin">Admin</option>
                              <option value="user">User</option>
                            </select>
                          ) : (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${u.role === 'admin' ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-700'}`}>
                              {u.role || 'user'}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {inactive ? (
                            <span className="inline-flex items-center gap-1 text-xs text-red-600">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500" />Disabled
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Active
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {!inactive && !isBullhornOnly && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={resettingUserId === u.id}
                                onClick={() => triggerUserPasswordReset(u)}
                                data-testid={`reset-password-button-${u.id}`}
                                title="Send password reset email"
                              >
                                {resettingUserId === u.id ? (
                                  <RefreshCw className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Key className="w-3 h-3" />
                                )}
                              </Button>
                            )}
                            {user?.role === 'admin' && !isSelf && (
                              inactive ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                                  onClick={async () => {
                                    try {
                                      await axios.post(`${API_URL}/api/tenants/${tenant?.id}/users/${u.id}/reactivate`);
                                      toast.success(`${u.name || u.email} has been re-enabled`);
                                      setTenantUsers(prev => prev.map(tu => tu.id === u.id ? { ...tu, is_active: true } : tu));
                                    } catch (err) {
                                      toast.error(getErrorMessage(err, 'Failed to reactivate user'));
                                    }
                                  }}
                                  data-testid={`enable-user-button-${u.id}`}
                                  title="Re-enable this user"
                                >
                                  <UserCheck className="w-3 h-3" />
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-200 hover:bg-red-50"
                                  onClick={async () => {
                                    if (!window.confirm(`Disable ${u.name || u.email}? They will no longer be able to log in.`)) return;
                                    try {
                                      await axios.delete(`${API_URL}/api/tenants/${tenant?.id}/users/${u.id}`);
                                      toast.success(`${u.name || u.email} has been disabled`);
                                      setTenantUsers(prev => prev.map(tu => tu.id === u.id ? { ...tu, is_active: false } : tu));
                                    } catch (err) {
                                      toast.error(getErrorMessage(err, 'Failed to disable user'));
                                    }
                                  }}
                                  data-testid={`disable-user-button-${u.id}`}
                                  title="Disable this user"
                                >
                                  <UserX className="w-3 h-3" />
                                </Button>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}

        {/* Security Settings */}
        {activeSection === 'users' && (
        <div className="neumorphic rounded-xl p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-lg">Security</h2>
              <p className="text-sm text-muted-foreground">Document link settings</p>
            </div>
          </div>

          <div className="space-y-4">
            <DefaultLinkExpiry />
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div>
                <p className="font-medium">Secure Token Encryption</p>
                <p className="text-sm text-muted-foreground">All links use JWT encryption</p>
              </div>
              <div className="flex items-center gap-2 text-emerald-600">
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-sm font-medium">Enabled</span>
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div>
                <p className="font-medium">Bullhorn Credentials</p>
                <p className="text-sm text-muted-foreground">API keys stored with AES-256 encryption</p>
              </div>
              <div className="flex items-center gap-2 text-emerald-600">
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-sm font-medium">Encrypted</span>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* UI Label Customisation */}
        {activeSection === 'branding' && <UILabelsSection tenantId={tenant?.id} />}

        {/* App Info */}
        {activeSection === 'general' && (
        <div className="neumorphic rounded-xl p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-terracotta flex items-center justify-center">
              <SettingsIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-lg">Application</h2>
              <p className="text-sm text-muted-foreground">About HyreDocs</p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Version</span>
              <span className="font-medium">2.0.0</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Environment</span>
              <span className="font-medium">Multi-Tenant SaaS</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Storage</span>
              <span className="font-medium">Cloud</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Email Service</span>
              <span className="font-medium">Email</span>
            </div>
          </div>
        </div>
        )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

/**
 * Feature flags card. Exposes the per-tenant Documents and Online
 * Forms package toggles — the same flags the sidebar's workspace
 * switcher gates on. Disabling both is server-rejected so an admin
 * can't accidentally empty their own sidebar.
 */
function FeaturesSection({ tenant, refreshTenant }) {
  const docsOn = tenant?.settings?.enable_documents !== false; // default true
  const formsOn = !!tenant?.settings?.enable_online_forms;
  const [busy, setBusy] = useState(false);

  const patch = async (payload) => {
    setBusy(true);
    try {
      await formsApi.updateFeatures(payload);
      await refreshTenant?.();
      toast.success('Features updated');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update features'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="neumorphic rounded-xl p-6" data-testid="settings-features-section">
      <div className="flex items-center gap-4 mb-5">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
          <Sparkles className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="font-heading font-semibold text-lg">Products</h2>
          <p className="text-sm text-muted-foreground">
            Toggle which workspaces appear in the sidebar. Disable one to give the team a
            focused experience — at least one must remain enabled.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-start justify-between p-4 border border-border rounded-lg" data-testid="settings-docs-toggle-row">
          <div className="flex-1 pr-4">
            <p className="font-medium text-foreground">Documents</p>
            <p className="text-sm text-muted-foreground mt-1">
              PDF document templates with bi-directional Bullhorn field sync — the original
              HyreDocs product. Includes the Templates, Send and Documents tracking pages.
            </p>
          </div>
          <Switch
            checked={docsOn}
            disabled={busy || (!formsOn && docsOn)} // can't disable the last product
            onCheckedChange={(v) => patch({ enable_documents: v })}
            data-testid="settings-docs-toggle"
          />
        </div>

        <div className="flex items-start justify-between p-4 border border-border rounded-lg" data-testid="settings-forms-toggle-row">
          <div className="flex-1 pr-4">
            <div className="flex items-center gap-2">
              <p className="font-medium text-foreground">Online Forms</p>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded">Beta</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Jotform-style online forms with multi-page support, conditional logic, AI
              generation, and Bullhorn write-back to the Files tab.
            </p>
          </div>
          <Switch
            checked={formsOn}
            disabled={busy || (!docsOn && formsOn)}
            onCheckedChange={(v) => patch({ enable_online_forms: v })}
            data-testid="settings-forms-toggle"
          />
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 * BullhornNotesSection — admin-only controls for the post-send
 * Bullhorn-note behaviour. Persists to ``tenant.settings`` via the
 * existing PUT ``/tenants/{id}/settings`` endpoint; consumed at
 * runtime by ``useBullhornNoteHandler``.
 *
 * Three knobs:
 *   1. Mode  (popup / toast / silent / off)
 *   2. Default note body — supports {placeholder} tokens
 *   3. Default note action type — dropdown populated live from
 *      Bullhorn's meta/Note?fields=action via /api/bullhorn/note-actions
 * ============================================================ */
function BullhornNotesSection({ tenant, refreshTenant }) {
  const settings = tenant?.settings || {};
  const [mode, setMode] = useState(settings.bullhorn_note_mode || 'popup');
  const [body, setBody] = useState(
    settings.bullhorn_note_default_body ||
      'Sent "{document_type}" via HyreDocs to {recipient_name} <{recipient_email}>.',
  );
  const [actionValue, setActionValue] = useState(
    settings.bullhorn_note_default_action || 'General',
  );
  const [actions, setActions] = useState([]);
  const [loadingActions, setLoadingActions] = useState(false);
  const [saving, setSaving] = useState(false);

  // Pull live note-actions from Bullhorn so the dropdown matches the
  // tenant's configured picklist. Falls back to a default list inside
  // the backend route so the UI stays usable even when Bullhorn is down.
  useEffect(() => {
    setLoadingActions(true);
    axios
      .get(`${BACKEND_URL}/api/bullhorn/note-actions`)
      .then((res) => setActions(res.data?.actions || []))
      .catch(() => setActions([]))
      .finally(() => setLoadingActions(false));
  }, []);

  const save = async () => {
    if (!tenant?.id) return;
    setSaving(true);
    try {
      await tenantsApi.updateSettings(tenant.id, {
        bullhorn_note_mode: mode,
        bullhorn_note_default_body: body,
        bullhorn_note_default_action: actionValue,
      });
      await refreshTenant?.();
      toast.success('Bullhorn note settings saved');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  const modeOptions = [
    {
      value: 'popup',
      title: 'Prompt with popup',
      desc: 'Show an editable dialog after every send. Recruiter can review, edit, save or skip.',
    },
    {
      value: 'toast',
      title: 'Auto-log with toast confirmation',
      desc: 'Note is logged automatically using the defaults below. A small toast confirms it.',
    },
    {
      value: 'silent',
      title: 'Auto-log silently',
      desc: 'Note is logged automatically using the defaults below. No popup, no toast.',
    },
    {
      value: 'off',
      title: 'Off — no note',
      desc: "Don't log any Bullhorn note on send. No popup either.",
    },
  ];

  return (
    <div className="neumorphic rounded-xl p-6" data-testid="bullhorn-notes-section">
      <div className="flex items-center gap-4 mb-5">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
          <StickyNote className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="font-heading font-semibold text-lg">Bullhorn Notes</h2>
          <p className="text-sm text-muted-foreground">
            Control what happens to the Bullhorn note that's logged when a recruiter sends a
            document or form. If auto-logging fails (e.g. token expired) the popup is shown
            instead so the note is never lost silently.
          </p>
        </div>
      </div>

      {/* Mode selector */}
      <div className="space-y-2 mb-6" data-testid="note-mode-options">
        <Label>When a document or form is sent…</Label>
        <div className="grid sm:grid-cols-2 gap-2">
          {modeOptions.map((opt) => {
            const active = mode === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setMode(opt.value)}
                className={`text-left p-3 rounded-lg border transition-colors ${
                  active
                    ? 'border-amber-500 bg-amber-50/60'
                    : 'border-border hover:border-amber-300 hover:bg-amber-50/20'
                }`}
                data-testid={`note-mode-${opt.value}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div
                    className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                      active ? 'border-amber-500 bg-amber-500' : 'border-slate-300'
                    }`}
                  >
                    {active && <div className="w-1.5 h-1.5 bg-white rounded-full m-auto mt-[3px]" />}
                  </div>
                  <p className="font-medium text-sm">{opt.title}</p>
                </div>
                <p className="text-xs text-muted-foreground ml-6">{opt.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Default body + action — only meaningful when something gets logged */}
      <div
        className={`space-y-5 ${mode === 'off' ? 'opacity-50 pointer-events-none' : ''}`}
        aria-disabled={mode === 'off'}
      >
        <div className="space-y-2">
          <Label htmlFor="default-note-body">Default note body</Label>
          <Textarea
            id="default-note-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            placeholder='Sent "{document_type}" via HyreDocs to {recipient_name} <{recipient_email}>.'
            data-testid="default-note-body-input"
          />
          <div className="text-xs text-muted-foreground space-y-1">
            <p>You can use these placeholder tokens — they're replaced when the note is created:</p>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {[
                '{document_type}',
                '{template_name}',
                '{form_name}',
                '{recipient_name}',
                '{recipient_email}',
                '{sender_name}',
              ].map((tok) => (
                <code
                  key={tok}
                  className="px-1.5 py-0.5 bg-white border border-slate-200 rounded text-[11px] font-mono cursor-pointer hover:bg-amber-50"
                  onClick={() => setBody((prev) => `${prev}${prev.endsWith(' ') || !prev ? '' : ' '}${tok}`)}
                  title="Click to append"
                >
                  {tok}
                </code>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="default-note-action">Default note action type</Label>
          <select
            id="default-note-action"
            value={actionValue}
            onChange={(e) => setActionValue(e.target.value)}
            disabled={loadingActions}
            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            data-testid="default-note-action-select"
          >
            {loadingActions && <option>Loading from Bullhorn…</option>}
            {!loadingActions && actions.length === 0 && (
              <option value={actionValue}>{actionValue}</option>
            )}
            {!loadingActions &&
              actions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label || opt.value}
                </option>
              ))}
          </select>
          <p className="text-xs text-muted-foreground">
            Pulled live from your Bullhorn Note action picklist.
          </p>
        </div>
      </div>

      <div className="flex justify-end pt-5 border-t border-border mt-5">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="save-note-settings-button"
        >
          {saving ? (
            <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving…</>
          ) : (
            <><Save className="w-4 h-4 mr-2" />Save Note Settings</>
          )}
        </Button>
      </div>
    </div>
  );
}


/* ============================================================
 * CredentialRemindersSection — admin controls for the daily
 * Bullhorn-credential expiry reminder emails.
 *
 * Persists to ``tenant.settings`` via the same updateSettings
 * endpoint as the other tenant-level prefs. The background
 * scanner (services/credential_reminders.py) reads these
 * three keys at run time:
 *   - credential_reminders_enabled
 *   - credential_reminder_days       (sorted desc, deduped, ints only)
 *   - credential_reminder_cc_recruiter
 *
 * UX: a tag-style input where the recruiter can add windows
 * (e.g. 90, 60, 30, 7, 1) and remove them with a click. We
 * pre-seed with the platform default [30, 7] which works for
 * the vast majority of UK construction credentials.
 * ============================================================ */
function CredentialRemindersSection({ tenant, refreshTenant }) {
  const settings = tenant?.settings || {};
  const [enabled, setEnabled] = useState(settings.credential_reminders_enabled !== false);
  const [windows, setWindows] = useState(
    Array.isArray(settings.credential_reminder_days) && settings.credential_reminder_days.length
      ? settings.credential_reminder_days
      : [30, 7]
  );
  const [ccRecruiter, setCcRecruiter] = useState(settings.credential_reminder_cc_recruiter !== false);
  const [newWindow, setNewWindow] = useState('');
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);

  const addWindow = () => {
    const n = parseInt(newWindow, 10);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error('Enter a positive number of days');
      return;
    }
    if (windows.includes(n)) {
      setNewWindow('');
      return;
    }
    setWindows([...windows, n].sort((a, b) => b - a));
    setNewWindow('');
  };

  const removeWindow = (n) => setWindows(windows.filter((w) => w !== n));

  const save = async () => {
    if (!tenant?.id) return;
    setSaving(true);
    try {
      await tenantsApi.updateSettings(tenant.id, {
        credential_reminders_enabled: enabled,
        credential_reminder_days: windows,
        credential_reminder_cc_recruiter: ccRecruiter,
      });
      await refreshTenant?.();
      toast.success('Reminder settings saved');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setTriggering(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/bullhorn/credentials/process-reminders`,
      );
      const sent = res.data?.sent ?? 0;
      const skipped = res.data?.skipped ?? 0;
      toast.success(`Reminders dispatched. Sent ${sent}, skipped ${skipped}.`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to trigger reminders'));
    } finally {
      setTriggering(false);
    }
  };

  return (
    <div className="neumorphic rounded-xl p-6" data-testid="credential-reminders-section">
      <div className="flex items-center gap-4 mb-5">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center">
          <Bell className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="font-heading font-semibold text-lg">Credential expiry reminders</h2>
          <p className="text-sm text-muted-foreground">
            Daily background job that emails candidates ahead of their Bullhorn credential expiring.
            Only fires for credentials created by HyreDocs templates with a credential mapping configured.
          </p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={setEnabled}
          data-testid="credential-reminders-enabled-switch"
        />
      </div>

      {enabled && (
        <div className="space-y-5">
          {/* Reminder windows */}
          <div className="space-y-2">
            <Label>Remind candidates this many days before expiry</Label>
            <p className="text-xs text-muted-foreground">
              Add one or more windows (e.g. 90, 30, 7). One email per window per credential — no duplicates.
            </p>
            <div className="flex flex-wrap items-center gap-2" data-testid="credential-reminder-windows-list">
              {windows.length === 0 ? (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  No reminder windows configured — nothing will fire. Add at least one.
                </p>
              ) : (
                windows.map((w) => (
                  <span
                    key={w}
                    className="inline-flex items-center gap-2 rounded-full bg-violet-100 text-violet-800 px-3 py-1 text-xs font-medium"
                    data-testid={`credential-reminder-window-${w}`}
                  >
                    {w} {w === 1 ? 'day' : 'days'} before
                    <button
                      type="button"
                      onClick={() => removeWindow(w)}
                      className="text-violet-500 hover:text-violet-900"
                      aria-label={`Remove ${w}-day window`}
                    >
                      ×
                    </button>
                  </span>
                ))
              )}
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Input
                type="number"
                min={1}
                value={newWindow}
                onChange={(e) => setNewWindow(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addWindow();
                  }
                }}
                placeholder="e.g. 14"
                className="w-32 h-9 text-sm"
                data-testid="credential-reminder-new-window-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addWindow}
                data-testid="credential-reminder-add-window"
              >
                Add window
              </Button>
            </div>
          </div>

          {/* CC recruiter */}
          <div className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="flex-1">
              <p className="text-sm font-medium">CC the recruiter who sent the document</p>
              <p className="text-xs text-muted-foreground">
                When ON, the recruiter who originally fired the document is BCC'd on every reminder so they know it went out.
              </p>
            </div>
            <Switch
              checked={ccRecruiter}
              onCheckedChange={setCcRecruiter}
              data-testid="credential-reminder-cc-recruiter-switch"
            />
          </div>

          <div className="text-xs text-muted-foreground bg-slate-50 border border-slate-200 rounded-md p-3">
            <strong>Where do credentials come from?</strong> Recruiters map a HyreDocs template to a
            Bullhorn credential under <em>Templates → Document Settings → Bullhorn Credential</em>. The expiry
            date is whichever date field on the template is tagged "Expiry date source" in that same dialog.
            On completion, HyreDocs creates the Candidate/Placement credential on Bullhorn — and from that
            point this scanner takes over.
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t">
        <Button
          variant="outline"
          onClick={runNow}
          disabled={triggering || !enabled}
          data-testid="credential-reminders-run-now"
        >
          {triggering ? (
            <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Running…</>
          ) : (
            'Run reminder scan now'
          )}
        </Button>
        <Button
          onClick={save}
          disabled={saving}
          className="btn-primary"
          data-testid="credential-reminders-save"
        >
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
          Save settings
        </Button>
      </div>
    </div>
  );
}

/**
 * ExpiringCredentialsSettingsSection — configures the placement-centric
 * Bullhorn credential-expiry dashboard (/:tenant/compliance). Distinct from
 * CredentialRemindersSection above: that one auto-emails candidates for
 * credentials HyreDocs itself created; this one is a manual-review dashboard
 * covering every Bullhorn credential on a currently-placed candidate.
 *
 * Both allowlists default empty on purpose — the dashboard shows nothing for
 * a tenant until an admin opts categories/statuses in here.
 */
function ExpiringCredentialsSettingsSection() {
  const [loaded, setLoaded] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [lookaheadDays, setLookaheadDays] = useState(90);
  const [includedCategoryIds, setIncludedCategoryIds] = useState([]);
  const [includedStatuses, setIncludedStatuses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      // Settled independently — the placement-status list needs a live
      // Bullhorn connection and will fail whenever one isn't configured
      // (e.g. local dev), but that must not also blank out the saved
      // enabled/lookahead/category settings, which don't depend on it.
      const [settingsRes, categoriesRes, statusesRes] = await Promise.allSettled([
        complianceApi.getSettings(),
        axios.get(`${BACKEND_URL}/api/credential-requests/bullhorn-categories`),
        bullhornApi.getFieldOptions('Placement', 'status'),
      ]);
      if (settingsRes.status === 'fulfilled') {
        const data = settingsRes.value.data;
        setEnabled(!!data?.enabled);
        setLookaheadDays(data?.lookahead_days ?? 90);
        setIncludedCategoryIds((data?.included_category_ids || []).map(String));
        setIncludedStatuses(data?.included_placement_statuses || []);
      } else {
        toast.error(getErrorMessage(settingsRes.reason, 'Failed to load compliance settings'));
      }
      if (categoriesRes.status === 'fulfilled') setCategories(categoriesRes.value.data?.categories || []);
      if (statusesRes.status === 'fulfilled') {
        setStatusOptions(statusesRes.value.data?.options || []);
      } else {
        console.warn('Placement status options unavailable:', getErrorMessage(statusesRes.reason));
      }
      setLoaded(true);
    })();
  }, []);

  const toggleCategory = (id) => {
    const key = String(id);
    setIncludedCategoryIds((prev) => prev.includes(key) ? prev.filter((v) => v !== key) : [...prev, key]);
  };
  const toggleStatus = (value) => {
    setIncludedStatuses((prev) => prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]);
  };

  const allStatusesSelected = statusOptions.length > 0 && statusOptions.every((opt) => includedStatuses.includes(opt.value));
  const toggleAllStatuses = () => {
    setIncludedStatuses(allStatusesSelected ? [] : statusOptions.map((opt) => opt.value));
  };

  const [categorySearch, setCategorySearch] = useState('');
  const filteredCategories = useMemo(() => {
    const q = categorySearch.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter((cat) => (cat.name || '').toLowerCase().includes(q) || String(cat.id).includes(q));
  }, [categories, categorySearch]);

  // "Select all" acts on whatever the search currently narrows to, so a
  // tenant with hundreds of categories can search "CSCS", select all of
  // those, then search again for another group — without losing the first
  // batch or being forced to select everything at once.
  const allCategoriesSelected = filteredCategories.length > 0 && filteredCategories.every((cat) => includedCategoryIds.includes(String(cat.id)));
  const toggleAllCategories = () => {
    const filteredIds = filteredCategories.map((cat) => String(cat.id));
    setIncludedCategoryIds((prev) => {
      if (allCategoriesSelected) return prev.filter((id) => !filteredIds.includes(id));
      return [...new Set([...prev, ...filteredIds])];
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await complianceApi.updateSettings({
        enabled,
        lookahead_days: Number(lookaheadDays) || 90,
        included_category_ids: includedCategoryIds,
        included_placement_statuses: includedStatuses,
      });
      setEnabled(!!data.enabled);
      setLookaheadDays(data.lookahead_days);
      setIncludedCategoryIds(data.included_category_ids || []);
      setIncludedStatuses(data.included_placement_statuses || []);
      toast.success('Compliance settings saved');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save compliance settings'));
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <div className="neumorphic rounded-xl p-6" data-testid="expiring-credentials-settings-section">
      <div className="flex items-center gap-4 mb-5">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-red-600 flex items-center justify-center">
          <ShieldAlert className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="font-heading font-semibold text-lg">Expiring Credentials dashboard</h2>
          <p className="text-sm text-muted-foreground">
            A live view of Bullhorn credentials nearing or past expiry for candidates on a qualifying placement.
            Bullhorn's credential module must be activated.
          </p>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="expiring-credentials-enabled-switch" />
      </div>

      {enabled && (
        <div className="space-y-5">
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label>Included placement statuses</Label>
              {statusOptions.length > 0 && (
                <button
                  type="button"
                  onClick={toggleAllStatuses}
                  className="text-xs font-medium text-amber-700 hover:text-amber-900"
                  data-testid="expiring-credentials-status-toggle-all"
                >
                  {allStatusesSelected ? 'Deselect all' : 'Select all'}
                </button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Only candidates with a placement in one of these statuses appear on the dashboard. Required — the dashboard stays empty until at least one is picked.
            </p>
            {statusOptions.length === 0 ? (
              <p className="text-xs text-muted-foreground">Could not load Placement statuses from Bullhorn.</p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="expiring-credentials-status-list">
                {statusOptions.map((opt) => {
                  const checked = includedStatuses.includes(opt.value);
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => toggleStatus(opt.value)}
                      className={`text-xs px-3 py-1.5 rounded-full border ${checked ? 'bg-amber-100 border-amber-300 text-amber-900 font-medium' : 'bg-white border-slate-200 text-muted-foreground'}`}
                      data-testid={`expiring-credentials-status-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label>Included credential categories</Label>
              {categories.length > 0 && (
                <button
                  type="button"
                  onClick={toggleAllCategories}
                  className="text-xs font-medium text-amber-700 hover:text-amber-900"
                  data-testid="expiring-credentials-category-toggle-all"
                >
                  {allCategoriesSelected ? 'Deselect all' : 'Select all'}
                </button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Which Bullhorn credential types show on the dashboard. Required — nothing shows until at least one is picked.
            </p>
            {categories.length > 0 && (
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  placeholder="Search credential categories…"
                  className="h-8 pl-8 text-sm"
                  data-testid="expiring-credentials-category-search"
                />
              </div>
            )}
            <div className="border rounded-md max-h-56 overflow-y-auto bg-slate-50" data-testid="expiring-credentials-category-list">
              {categories.length === 0 ? (
                <p className="p-3 text-xs text-muted-foreground">No credential categories available. Check Bullhorn connection.</p>
              ) : filteredCategories.length === 0 ? (
                <p className="p-3 text-xs text-muted-foreground">No credential categories match "{categorySearch}".</p>
              ) : (
                filteredCategories.map((cat) => {
                  const checked = includedCategoryIds.includes(String(cat.id));
                  return (
                    <label
                      key={cat.id}
                      className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer border-b last:border-b-0 hover:bg-white ${checked ? 'bg-amber-50' : ''}`}
                      data-testid={`expiring-credentials-cat-${cat.id}`}
                    >
                      <input type="checkbox" checked={checked} onChange={() => toggleCategory(cat.id)} />
                      <span className="truncate flex-1">{cat.name?.replace(/^\s+/, '')}</span>
                      <span className="text-xs text-muted-foreground">#{cat.id}</span>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Lookahead window</Label>
            <p className="text-xs text-muted-foreground">How far ahead the dashboard shows upcoming expiries.</p>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={1}
                value={lookaheadDays}
                onChange={(e) => setLookaheadDays(e.target.value)}
                className="w-28 h-9 text-sm"
                data-testid="expiring-credentials-lookahead-input"
              />
              <span className="text-sm text-muted-foreground">days</span>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t">
        <Button onClick={save} disabled={saving} className="btn-primary" data-testid="expiring-credentials-save">
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
          Save settings
        </Button>
      </div>
    </div>
  );
}


/**
 * UILabelsSection — Admins can rename key UI labels per tenant
 * (e.g. "Credentials" → "Qualifications").
 */
function UILabelsSection({ tenantId }) {
  const [labels, setLabels] = useState({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    (async () => {
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/api/settings/ui-labels/me`,
        );
        setLabels(data || {});
        setLoaded(true);
      } catch { setLoaded(true); }
    })();
  }, [tenantId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${BACKEND_URL}/api/settings/ui-labels`,
        labels,
      );
      setLabels(data || {});
      toast.success('UI labels saved');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save labels'));
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  const labelEntries = [
    { key: 'credentials', desc: 'The "Credentials" section name throughout the app' },
    { key: 'documents', desc: 'The "Documents" section name' },
    { key: 'forms', desc: 'The "Forms" section name' },
    { key: 'templates', desc: 'The "Templates" section name' },
    { key: 'send', desc: 'The "Send" page label' },
    { key: 'portal_title', desc: 'Candidate portal header subtitle' },
  ];

  return (
    <div className="neumorphic rounded-xl p-6" data-testid="settings-ui-labels-section">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-indigo-600 flex items-center justify-center">
          <Palette className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="font-heading font-semibold text-lg">UI Label Customisation</h2>
          <p className="text-sm text-muted-foreground">Rename sections and labels across the app for your organisation</p>
        </div>
      </div>

      <div className="space-y-4">
        {labelEntries.map(({ key, desc }) => (
          <div key={key} className="grid grid-cols-3 gap-3 items-center">
            <div>
              <Label className="text-sm font-medium capitalize">{key.replace(/_/g, ' ')}</Label>
              <p className="text-xs text-muted-foreground">{desc}</p>
            </div>
            <div className="col-span-2">
              <Input
                value={labels[key] || ''}
                onChange={(e) => setLabels(prev => ({ ...prev, [key]: e.target.value }))}
                placeholder={key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}
                data-testid={`ui-label-${key}`}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary"
          data-testid="ui-labels-save"
        >
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Labels
        </Button>
      </div>
    </div>
  );
}
